<template>
  <div class="res">
    <h1 class="res-h1">
      My reservations
    </h1>
    <p class="res-lead">
      Every date we've booked into your venue. Reservations can still change; we'll reflect that here and send it
      through your booking system, email or SMS as you've configured.
    </p>

    <div class="res-stack">
      <ds-note
        v-if="loadFailed"
        variant="danger"
      >
        We couldn't load your reservations. What you see here may be incomplete or out of date. We'll keep
        retrying every few minutes.
      </ds-note>

      <ds-tabs
        v-model="view"
        :tabs="tabs"
        id-prefix="res"
      />

      <!-- List: the default, and where most partners only ever need to look. Upcoming and
           past are separate cards rather than one table, because a date that already
           happened isn't the same kind of row: nothing can be done about it, and reading
           back past the top of the list made the dates look mis-sorted. One v-for so the
           cell templates are written once. -->
      <div
        v-if="view === 'list'"
        id="res-panel-list"
        class="res-panel"
        role="tabpanel"
        aria-labelledby="res-tab-list"
        tabindex="0"
      >
        <ds-card
          v-for="section in listSections"
          :key="section.key"
          :title="section.title"
          :subtitle="section.subtitle"
          flush
          class="res-section"
          :class="[`res-section-${section.key}`, { collapsed: section.collapsible && !pastOpen }]"
        >
          <!-- Collapsed by default: past dates are worth keeping, not worth scrolling
               past every visit. -->
          <template
            v-if="section.collapsible"
            #actions
          >
            <button
              type="button"
              class="res-expander"
              :class="{ open: pastOpen }"
              :aria-expanded="pastOpen ? 'true' : 'false'"
              :aria-label="pastOpen ? 'Hide past reservations' : 'Show past reservations'"
              aria-controls="res-past-table"
              @click="pastOpen = !pastOpen"
            >
              <feather-icon icon="ChevronDownIcon" />
            </button>
          </template>

          <!-- v-show, not v-if, so aria-controls always resolves to a real element. -->
          <ds-table
            v-show="!section.collapsible || pastOpen"
            :id="section.collapsible ? 'res-past-table' : null"
            :fields="section.fields"
            :items="section.items"
            :busy="section.busy"
            busy-text="Loading reservations…"
            :empty-text="section.emptyText"
          >
            <!-- Time only: the day divider above the row already carries the date. -->
            <template #cell(time)="{ item }">
              <span class="res-time">{{ formattedDate(item.start, 'HH:mm') }}</span>
            </template>

            <!-- Wraps rather than stretching the row: the deal name is a sentence,
                 not a label. -->
            <template #cell(type)="{ item }">
              <span class="res-wrap">{{ item.extendedProps.dateType }}</span>
            </template>

            <template #cell(details)="{ item }">
              {{ item.extendedProps.label }}
            </template>

            <!-- Unknown reads as a blank cell, matching DsTable's own fallback, rather
                 than a confident "0 persons". -->
            <template #cell(tableSize)="{ item }">
              <ds-badge
                v-if="item.extendedProps.partySize"
                variant="wine"
              >
                {{ item.extendedProps.partySize }}
                {{ item.extendedProps.partySize === 1 ? 'person' : 'persons' }}
              </ds-badge>
              <template v-else>
                <span aria-hidden="true">-</span><span class="ds-sr-only">Not set</span>
              </template>
            </template>

            <!-- Only the upcoming section carries this column; a past date can't be
                 cancelled, so the past table has no actions field to fill. -->
            <template #cell(actions)="{ item }">
              <div class="res-actions">
                <ds-button
                  variant="danger"
                  size="sm"
                  @click="openDecline(item)"
                >
                  Cancel reservation
                </ds-button>
              </div>
            </template>
          </ds-table>
        </ds-card>
      </div>

      <!-- Calendar: v-show, not v-if, because unmounting would throw away the month the
           partner navigated to every time they glanced at the list. -->
      <ds-card
        v-show="view === 'calendar'"
        id="res-panel-calendar"
        role="tabpanel"
        aria-labelledby="res-tab-calendar"
        tabindex="0"
      >
        <div class="res-calendar-view">
          <div class="res-calendar">
            <full-calendar
              ref="refCalendar"
              :options="calendarOptions"
              class="full-calendar"
            />
          </div>

          <aside class="res-side">
            <b-calendar
              v-model="selectedDate"
              hide-header
              locale="en"
              @selected="goToDate"
            />
            <div class="res-side-summary">
              <div class="res-side-date">
                {{ formattedDate(selectedDate, 'MMMM D, YYYY') }}
              </div>
              <div class="res-side-count">
                {{ selectedDateReservations.length }}
                {{ selectedDateReservations.length === 1 ? 'reservation' : 'reservations' }}
              </div>

              <!-- The count on its own left the partner to hunt the grid for what it counted. -->
              <ul
                v-if="selectedDateReservations.length"
                class="res-side-list"
              >
                <li
                  v-for="reservation in selectedDateReservations"
                  :key="reservation.id"
                >
                  <button
                    type="button"
                    class="res-side-item"
                    @click="openDetails(reservation)"
                  >
                    <span class="res-side-item-time">{{ formattedDate(reservation.start, 'HH:mm') }}</span>
                    <span class="res-side-item-couple">
                      {{ reservation.extendedProps.label }}
                    </span>
                    <span class="res-side-item-type">{{ reservation.extendedProps.dateType }}</span>
                  </button>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </ds-card>
    </div>

    <!-- Modals -->
    <date-details
      :show.sync="showDateDetails"
      :date="clickedReservation"
    />

    <date-decline
      :show.sync="showDateDecline"
      :date="clickedReservation"
      @cancel="handleCancellation"
    />
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid'
import moment from 'moment'
import { BCalendar } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import {
  DsBadge, DsButton, DsCard, DsNote, DsTable, DsTabs,
} from '@/design-system/components'
import { colors } from '@/design-system'
import DateDetails from '@/components/modals/DateDetails.vue'
import DateDecline from '@/components/modals/DateDecline.vue'
import { requests } from '@/_helpers/requests'

const REFETCH_MS = 300000
// The most the search hands out per page.
const PAGE_LIMIT = 100
// Never a cancel, even if the search stopped leaving them out. No status filter: the search drops failed
// bookings itself, and keeps a time change whose old booking failed to cancel, as the portal always has.
const SEARCH_ACTIONS = ['reserve', 'update']

export default {
  name: 'Reservations',
  components: {
    BCalendar,
    DateDecline,
    DateDetails,
    DsBadge,
    DsButton,
    DsCard,
    DsNote,
    DsTable,
    DsTabs,
    FullCalendar,
  },
  data() {
    return {
      // List first: it answers "what's coming up" without navigating anywhere.
      view: 'list',
      clickedReservation: {},
      selectedDate: new Date(),
      // Stamped forward by the refetch timer. Everything that splits upcoming from past,
      // or names a day "Today", reads this rather than the clock, so the split moves with
      // the day on a page that stays open.
      now: new Date(),
      pastOpen: false,
      showDateDetails: false,
      showDateDecline: false,
      loading: true,
      loadFailed: false,
      reservations: [],
      refetchTimer: null,
      listFields: [
        { key: 'time', label: 'Time', width: '90px' },
        { key: 'type', label: 'Date type' },
        { key: 'details', label: 'Reservation details' },
        { key: 'tableSize', label: 'Table size' },
        { key: 'actions', label: 'Actions', labelSrOnly: true },
      ],
      // Same columns, minus the action: a date that already happened can't be cancelled.
      pastFields: [
        { key: 'time', label: 'Time', width: '90px' },
        { key: 'type', label: 'Date type' },
        { key: 'details', label: 'Reservation details' },
        { key: 'tableSize', label: 'Table size' },
      ],
      // CALENDAR SETTINGS
      calendarOptions: {
        // CALENDAR LAYOUT
        allDaySlot: false,
        plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin],
        initialView: 'timeGridWeek',
        headerToolbar: {
          start: 'prev,next title',
          // listMonth dropped: the List tab is that view, and better.
          end: 'dayGridMonth,timeGridWeek,timeGridDay',
        },
        views: {
          // Month grid can't show times side by side, so cap the chips and put the
          // rest behind a popover instead of silently hiding them.
          dayGridMonth: {
            dayMaxEvents: 3,
            eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
          },
        },
        moreLinkClick: 'popover',
        contentHeight: 'auto',
        timeZone: 'local',
        slotMinTime: '09:00:00',
        slotDuration: '00:60:00',
        slotEventOverlap: false,
        slotLabelFormat: {
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false,
          hour12: false,
        },
        // EVENTS
        events: [],
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false,
          hour12: false,
        },
        eventDisplay: 'block',
        eventBackgroundColor: colors.wine,
        eventBorderColor: colors.wine,
        eventClassNames: ['font-weight-normal', 'p-0'],
        eventContent: this.createCustomContent,
        eventClick: this.handleReservationClick,
        // The day's count beside its date, e.g. "Thu 10/16 (6)": the sidebar that counts
        // them is hidden on phones and tablets.
        dayHeaderContent: this.dayHeaderWithCount,
        dayCellContent: this.dayNumberWithCount,
        // DATE CHANGE
        datesSet: this.handleNextPrevDay,
        dateClick: this.handleDateClick,
        navLinks: true,
        navLinkDayClick: this.handleLinkDayClick,
      },
    }
  },
  computed: {
    ...mapGetters({
      location: 'authentication/location',
    }),
    tabs() {
      // The upcoming count, not the total: past reservations are reference, and a tab
      // reading 9 above a table of 4 rows is the kind of mismatch people report as a bug.
      return [
        { key: 'list', label: 'List', count: this.loading ? null : this.upcomingReservations.length },
        { key: 'calendar', label: 'Calendar' },
      ]
    },
    // Soonest first. Today counts as upcoming for the whole day, so a reservation this
    // evening doesn't drop into "past" at midday.
    upcomingReservations() {
      return this.reservations
        .filter(r => new Date(r.start) >= this.startOfToday)
        .sort((a, b) => new Date(a.start) - new Date(b.start))
    },
    // Most recent first: the further back it is, the less anyone is looking for it.
    pastReservations() {
      return this.reservations
        .filter(r => new Date(r.start) < this.startOfToday)
        .sort((a, b) => new Date(b.start) - new Date(a.start))
    },
    // The cards the list tab renders, in order. Past only appears once there is some, so
    // a new venue never sees an empty "nothing has happened yet" card.
    listSections() {
      const sections = [{
        key: 'upcoming',
        title: '',
        subtitle: '',
        fields: this.listFields,
        items: this.withDayDividers(this.upcomingReservations),
        busy: this.loading,
        emptyText: 'No reservations booked yet.',
        collapsible: false,
      }]

      if (!this.loading && this.pastReservations.length) {
        const count = this.pastReservations.length
        sections.push({
          key: 'past',
          title: 'Past reservations',
          subtitle: `${count} ${count === 1 ? 'date' : 'dates'} already held at your venue.`,
          fields: this.pastFields,
          items: this.withDayDividers(this.pastReservations),
          busy: false,
          emptyText: '',
          collapsible: true,
        })
      }

      return sections
    },
    // Derived from `now` rather than calling new Date() here: a computed with no reactive
    // dependency is evaluated once and cached for the component's lifetime, so a portal
    // left open past midnight would keep yesterday in Upcoming, under a "Yesterday"
    // divider, still offering to cancel it.
    startOfToday() {
      const start = new Date(this.now)
      start.setHours(0, 0, 0, 0)
      return start
    },
    // Grouped once per load, so the sidebar and every calendar day cell look up their day
    // rather than each scanning all reservations.
    reservationsByDay() {
      const byDay = {}
      const sorted = [...this.reservations].sort((a, b) => new Date(a.start) - new Date(b.start))
      sorted.forEach(reservation => {
        const day = moment(reservation.start).format('YYYY-MM-DD')
        if (!byDay[day]) byDay[day] = []
        byDay[day].push(reservation)
      })
      return byDay
    },
    // Drives both the count and the list in the sidebar.
    selectedDateReservations() {
      return this.reservationsByDay[moment(this.selectedDate).format('YYYY-MM-DD')] || []
    },
  },
  watch: {
    location(val) {
      // get reservations when location has id
      if (val.id) this.getReservations()
    },
    view(next) {
      // While hidden the calendar can't measure itself, so re-sync on the way back.
      if (next !== 'calendar') return
      this.$nextTick(() => {
        if (this.$refs.refCalendar) this.$refs.refCalendar.getApi().updateSize()
      })
    },
  },
  mounted() {
    this.getReservations()
    this.refetchTimer = setInterval(this.tick, REFETCH_MS)
  },
  beforeDestroy() {
    // The old interval was never cleared, so it kept polling after leaving the page.
    if (this.refetchTimer) clearInterval(this.refetchTimer)
  },
  methods: {
    // Restamping `now` is the point of doing this here rather than passing getReservations
    // straight to setInterval: the day boundary has to move even when the fetch is a no-op.
    tick() {
      this.now = new Date()
      this.getReservations()
    },
    getReservations() {
      if (!this.location?.id) {
        // Otherwise the table sits on "Loading reservations…" instead of the empty state.
        this.loading = false
        return
      }
      this.searchAllPages(this.location.id)
        .then(rawReservations => {
          this.setReservations(rawReservations)
          this.loadFailed = false
        })
        .catch(() => {
          // Only toast when entering the failed state, so the 5 minute poll
          // doesn't stack a toast every cycle while the API is down. The
          // banner is what communicates the ongoing condition.
          if (!this.loadFailed) {
            this.$notify.error('Failed to load reservations')
          }
          this.loadFailed = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    // The search starts 3 days back on its own, which is how far past dates go.
    searchAllPages(locationId, cursor = null, collected = []) {
      const body = { actions: SEARCH_ACTIONS, limit: PAGE_LIMIT }
      if (cursor) body.cursor = cursor
      return requests.post(`partner-portal/locations/${locationId}/reservations/search`, body)
        .then(response => {
          const rows = collected.concat(response.data.list)
          const next = response.data.nextCursor
          return next ? this.searchAllPages(locationId, next, rows) : rows
        })
    },
    goToDate(date) {
      // The calendar only exists while its tab is open.
      if (this.$refs.refCalendar) this.$refs.refCalendar.getApi().gotoDate(new Date(date))
    },
    handleNextPrevDay(dateInfo) {
      if (dateInfo.view.type === 'timeGridDay') this.selectedDate = dateInfo.startStr
    },
    handleReservationClick(data) {
      this.clickedReservation = data.event
      // classList rather than an exact className match, so an added utility class can't
      // silently turn "decline" into "details".
      const target = data.jsEvent.target
      if (target.classList && target.classList.contains('custom-close')) this.showDateDecline = true
      else this.showDateDetails = true
    },
    openDetails(reservation) {
      this.clickedReservation = reservation
      this.showDateDetails = true
    },
    openDecline(reservation) {
      this.clickedReservation = reservation
      this.showDateDecline = true
    },
    handleDateClick(dateInfo) {
      this.selectedDate = dateInfo.dateStr
    },
    handleLinkDayClick(date) {
      this.selectedDate = date
    },
    formattedDate(date, format) {
      return moment(date).format(format)
    },
    // A day header before each new date, so the eye has somewhere to land and no row has
    // to repeat the date.
    withDayDividers(reservations) {
      const rows = []
      let currentDay = null
      reservations.forEach(reservation => {
        const day = moment(reservation.start).format('YYYY-MM-DD')
        if (day !== currentDay) {
          currentDay = day
          rows.push({ id: `day-${day}`, divider: true, label: this.dayLabel(reservation.start) })
        }
        rows.push(reservation)
      })
      return rows
    },
    // The divider is the only place the date appears, so it carries the full one. Named
    // days drop the weekday, since "Today" already says which day it is.
    dayLabel(date) {
      const day = moment(date).startOf('day')
      const diff = day.diff(moment(this.now).startOf('day'), 'days')
      if (diff === 0) return `Today, ${day.format('D MMMM YYYY')}`
      if (diff === 1) return `Tomorrow, ${day.format('D MMMM YYYY')}`
      if (diff === -1) return `Yesterday, ${day.format('D MMMM YYYY')}`
      return day.format('dddd D MMMM YYYY')
    },
    // Built as DOM nodes rather than innerHTML: the names come from the API, and
    // interpolating them into markup is an XSS sink.
    createCustomContent(arg) {
      const wrapper = document.createElement('div')
      wrapper.classList.add('d-flex', 'justify-content-between', 'h-100')

      const label = document.createElement('div')
      label.classList.add('overflow-hidden')
      label.appendChild(document.createTextNode(moment(arg.event.start).format('HH:mm')))
      label.appendChild(document.createElement('br'))
      const { label: name } = arg.event.extendedProps
      label.appendChild(document.createTextNode(name))
      wrapper.append(label)

      // A button, not an <a> with no href: this is what opens the decline flow, so it has
      // to be keyboard reachable and announced as something other than "times".
      const close = document.createElement('button')
      close.type = 'button'
      close.classList.add('custom-close')
      close.setAttribute('aria-label', `Cancel the ${name} reservation`)
      close.textContent = '×'

      wrapper.append(close)
      return { domNodes: [wrapper] }
    },
    // Assigned once at the end: pushing row by row re-rendered the table per reservation
    // on every 5-minute poll.
    setReservations(rawReservations) {
      this.reservations = rawReservations.map((raw, index) => {
        const start = new Date(raw.reservationTime)
        const isGroupDate = raw.kind === 'group-date'
        return {
          // Required for FullCalendar
          id: index,
          title: raw.dealName,
          start,
          end: moment(new Date(start).setHours(start.getHours() + 2)).format(),
          textColor: 'white',
          // Optional for FullCalendar
          extendedProps: {
            reservationId: raw.id,
            // The daters' names for a date, the name the location was given for a group date table.
            label: raw.label,
            dateType: isGroupDate ? 'Group date' : raw.dealName,
            // "0 persons" is worse than an empty cell.
            partySize: raw.partySize || null,
          },
        }
      })
      this.calendarOptions.events = this.reservations
    },
    // Month headers are weekdays ("Thu"), not a single day, so they get no count.
    dayHeaderWithCount(arg) {
      return arg.view.type === 'dayGridMonth' ? arg.text : this.withDayCount(arg.text, arg.date)
    },
    // Time-grid columns render this hook too, with an empty dayNumberText to leave alone.
    dayNumberWithCount(arg) {
      return arg.dayNumberText && this.withDayCount(arg.dayNumberText, arg.date)
    },
    withDayCount(text, date) {
      const count = (this.reservationsByDay[moment(date).format('YYYY-MM-DD')] || []).length
      return count ? `${text} (${count})` : text
    },
    handleCancellation(cancellationData) {
      const reservationId = this.clickedReservation.extendedProps.reservationId
      requests.put(`partner-portal/reservations/${reservationId}/cancel`, {
        reason: cancellationData.reason,
        details: cancellationData.details,
      })
        .then(() => {
          this.getReservations()
        })
        .catch(() => {
          this.$notify.error('Failed to cancel reservation')
        })
    },
  },
}
</script>

<style lang="scss">
// FullCalendar's base layout. The design-system overrides live in the scoped block below.
@import "@core/scss/vue/apps/calendar.scss";
</style>

<style lang="scss" scoped>
@import '~@/design-system/mixins';

.res {
  max-width: 1140px;
  margin: 0 auto;
  color: $c-text;
}

.res-h1 {
  @include ds-page-title;
}

.res-lead {
  @include ds-lead;
}

.res-stack {
  @include ds-stack;

  align-items: stretch;
}

.ds-tabs {
  align-self: flex-start;
}

.res-panel {
  @include ds-stack;
}

.res-time {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

// DsTable's own copy is scoped to that component, so a cell slot needs its own.
.ds-sr-only {
  @include ds-sr-only;
}

.res-expander {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid $c-border;
  border-radius: 9px;
  background: $c-white;
  color: $c-text-sec;
  cursor: pointer;

  &:hover {
    background: $c-bg;
    color: $c-text;
  }

  // Same split as DsButton: drop the UA's blue ring on :focus, keep ours for keyboards.
  &:focus { outline: none; }

  &:focus-visible { @include ds-focus-ring; }

  ::v-deep svg {
    width: 17px;
    height: 17px;
    transition: transform 0.18s ease;
  }

  &.open ::v-deep svg {
    transform: rotate(180deg);
  }
}

// Collapsed, the card is only its header, so the divider under it would underline nothing.
.res-section.collapsed ::v-deep .ds-card-head {
  border-bottom: none;
}

// Past dates are reference, not work: same layout, dialled back so the upcoming card
// stays the one that draws the eye.
.res-section-past {
  ::v-deep .ds-table td {
    color: $c-text-sec;
  }

  ::v-deep .res-time {
    color: $c-text-sec;
  }

  ::v-deep .ds-badge {
    opacity: 0.7;
  }
}

// DsTable keeps cells on one line; this column is prose, so let it wrap.
.res-wrap {
  display: inline-block;
  max-width: 280px;
  white-space: normal;
}

.res-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.res-calendar-view {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.res-calendar {
  flex: 1 1 auto;
  min-width: 0;
}

.res-side {
  flex: 0 0 268px;

  // Was a duplicated mobile template branch; one responsive rule covers it.
  @media (max-width: 991px) {
    display: none;
  }
}

// b-calendar paints the selected day with .btn-primary; these bring the picker onto the
// palette. Selectors mirror bootstrap-vue's own so they win without extra weight.
.res-side ::v-deep {
  .b-calendar-grid {
    border: 1px solid $c-border;
    border-radius: 12px;
    box-shadow: none;
  }

  .b-calendar-grid-caption {
    font-size: 13.5px;
    font-weight: 700;
    color: $c-text;
  }

  .b-calendar-grid-weekdays small {
    @include ds-eyebrow;

    font-size: 10px;
  }

  .b-calendar-nav .btn {
    color: $c-text-sec;

    &:hover {
      background: $c-bg;
      color: $c-text;
    }
  }

  // Selected day.
  .b-calendar-grid-body .btn.active {
    background-color: $c-wine !important;
    border-color: $c-wine !important;
    color: $c-white !important;
  }

  // Today, when it isn't the selected day.
  .b-calendar-grid-body .btn:not(.active) {
    color: $c-text;

    &:hover {
      background-color: $c-wine-light;
      color: $c-wine;
    }
  }

  .b-calendar-grid-body .text-muted {
    color: $c-text-ter !important;
  }

  // Bootstrap-vue's keyboard hint. Kept (it is the picker's a11y affordance) but quietened.
  .b-calendar-grid-help {
    border-top-color: $c-border-light !important;
    background: $c-white !important;
    color: $c-text-ter !important;
    font-size: 10.5px;
  }
}

.res-side-summary {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid $c-border-light;
}

.res-side-date {
  font-weight: 700;
  font-size: 14px;
}

.res-side-count {
  margin-top: 2px;
  font-size: 12.5px;
  color: $c-text-sec;
}

.res-side-list {
  margin: 12px 0 0;
  padding: 0;
  list-style: none;

  li + li {
    margin-top: 6px;
  }
}

.res-side-item {
  display: block;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid $c-border-light;
  border-radius: 10px;
  background: $c-white;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: $c-wine;
    background: $c-wine-light;
  }

  &:focus-visible {
    @include ds-focus-ring;
  }
}

.res-side-item-time {
  font-size: 12px;
  font-weight: 700;
  color: $c-wine;
}

.res-side-item-couple {
  display: block;
  margin-top: 1px;
  font-size: 12.5px;
  color: $c-text;
}

.res-side-item-type {
  display: block;
  font-size: 11.5px;
  color: $c-text-sec;
}

// ---------------------------------------------------------------------------
// FullCalendar, on the design system. ::v-deep because the grid, toolbar and
// event chips are all rendered outside this component's template.
//
// Some of these tie the vendor's specificity exactly and win only because this
// block is emitted after the @import in the unscoped block above. If that import
// ever moves (to main.js, say), these need !important or a heavier selector.
// ---------------------------------------------------------------------------
.res-calendar ::v-deep {
  // ---------- Toolbar ----------
  .fc-toolbar {
    align-items: center;
    margin-bottom: 18px;
  }

  .fc-toolbar-title {
    @include ds-title;

    font-size: 17px;
  }

  // prev / next as quiet icon buttons.
  .fc-prev-button,
  .fc-next-button {
    // !important because app-calendar.scss sets `.fc-prev-button { padding-left: 0
    // !important }`, which would leave the arrow off-centre inside this border.
    padding: 4px 8px !important;
    border: 1px solid $c-border;
    border-radius: 8px;
    background: $c-white;
    color: $c-text-sec;
    box-shadow: none;

    &:hover {
      background: $c-bg;
      color: $c-text;
    }

    &:active {
      background: $c-bg !important;
      color: $c-text !important;
    }

    // The vendor zeroes the focus shadow; put the design system's ring back rather than
    // leaving keyboard users with no indication at all.
    &:focus-visible {
      @include ds-focus-ring;
    }
  }

  .fc-toolbar-chunk:first-child {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  // Month / Week / Day as the same segmented control as DsTabs.
  .fc-button-group {
    gap: 3px;
    padding: 4px;
    background: $c-white;
    border: 1px solid $c-border;
    border-radius: 12px;

    // Mirrors the :not() chain in @core/scss/base/pages/app-calendar.scss, which sets the
    // active state to rgba($primary, 0.2) !important. Matching its shape is what wins.
    .fc-button-primary:not(.fc-prev-button):not(.fc-next-button) {
      padding: 6px 14px;
      border: none !important;
      border-radius: 9px !important;
      background-color: transparent !important;
      color: $c-text-sec !important;
      font-size: 12.5px;
      font-weight: 600;
      text-transform: capitalize;
      box-shadow: none !important;

      &:hover {
        background-color: $c-bg !important;
        border-color: transparent !important;
        color: $c-text !important;
      }

      // Same focus treatment as DsTabs, which this control is meant to match.
      &:focus-visible {
        @include ds-focus-ring;

        box-shadow: 0 0 0 3px rgba($c-orange, 0.28) !important;
      }

      &.fc-button-active {
        background-color: $c-wine !important;
        border-color: $c-wine !important;
        color: $c-white !important;
      }
    }
  }

  // ---------- Grid ----------
  .fc-theme-standard .fc-scrollgrid,
  .fc-theme-standard td,
  .fc-theme-standard th {
    border-color: $c-border-light;
  }

  .fc-col-header-cell {
    padding: 10px 0;
    background: $c-bg;

    .fc-col-header-cell-cushion {
      @include ds-eyebrow;

      font-size: 10.5px;
      color: $c-text-sec;
      text-decoration: none;
    }
  }

  .fc-timegrid-slot-label-cushion,
  .fc-timegrid-axis-cushion {
    font-size: 11px;
    font-weight: 500;
    color: $c-text-ter;
  }

  // Bootstrap's today highlight is a heavy yellow; a wine tint is quieter.
  .fc-day-today {
    background: $c-wine-light !important;
  }

  .fc-daygrid-day-number {
    padding: 6px 8px;
    font-size: 12px;
    font-weight: 600;
    color: $c-text;
    text-decoration: none;
  }

  .fc-timegrid-now-indicator-line {
    border-color: $c-orange;
  }

  .fc-timegrid-now-indicator-arrow {
    border-top-color: $c-orange;
    border-bottom-color: $c-orange;
    border-left-color: $c-orange;
  }

  // ---------- Events ----------
  .fc-event {
    padding: 3px 6px;
    border-radius: 7px;
    font-size: 11.5px;
    line-height: 1.35;
    box-shadow: none;
    cursor: pointer;

    // brightness() is multiplicative, so on $c-wine (#29061b) it is imperceptible.
    &:hover {
      background-color: $c-wine-mid !important;
      border-color: $c-wine-mid !important;
    }
  }

  .fc-daygrid-event {
    padding: 2px 6px;
  }

  .custom-close {
    position: absolute;
    top: 0;
    right: 2px;
    padding: 0 2px;
    border: none;
    background: none;
    font-size: 1.4rem;
    line-height: 16px;
    color: rgba($c-white, 0.65);
    cursor: pointer;

    &:hover { color: $c-white; }
  }

  // ---------- "+2 more" popover ----------
  .fc-daygrid-more-link {
    font-size: 11px;
    font-weight: 700;
    // Not $c-orange: at 11px on white that is ~3.0:1, under WCAG AA.
    color: $c-wine;
  }

  .fc-popover {
    border: 1px solid $c-border;
    border-radius: 12px;
    box-shadow: 0 14px 36px rgba($c-wine, 0.16);
    overflow: hidden;
  }

  .fc-popover-header {
    padding: 9px 12px;
    background: $c-bg;
    color: $c-text;
    font-size: 12px;
    font-weight: 700;
  }

  .fc-popover-body {
    padding: 8px;
  }
}
</style>
